// pages/EstimationPage.jsx
import React, { useState, useEffect } from "react";
import i18next from "i18next";
import { useLocation, Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, FileText, TrendingUp, Shield, Users, CheckCircle, ArrowRight, Calculator, Scale, Zap, Clock, } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { getMediumLabel } from "../utils/artkivLabels";
const getLocaleCode = () => {
    const language = i18next.resolvedLanguage || i18next.language || "fr";
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
};
export default function EstimationPage() {
    const location = useLocation();
    const { user } = useAuth();
    const { t } = useTranslation();
    const [artworks, setArtworks] = useState([]);
    const [selectedArtwork, setSelectedArtwork] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    // Filtres expert / collectionneur
    const [artistFilter, setArtistFilter] = useState("");
    const [mediumFilter, setMediumFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [collectionFilter, setCollectionFilter] = useState("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (user?.id) {
            loadArtworks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);
    useEffect(() => {
        if (location.state?.artworkId) {
            const artwork = artworks.find((a) => a.id === location.state.artworkId);
            if (artwork) {
                setSelectedArtwork(artwork);
                setCurrentStep(2);
            }
        }
    }, [location.state, artworks]);
    const getArtworkCollectionLabel = (artwork) => artwork?.sub_collection ||
        artwork?.subcollection ||
        artwork?.sub_collection_name ||
        artwork?.subcollection_name ||
        artwork?.collection_name ||
        artwork?.collection ||
        "";
    const filteredArtworks = artworks.filter((artwork) => {
        const collectionLabel = getArtworkCollectionLabel(artwork);
        const matchesArtist = !artistFilter ||
            artwork.artist?.toLowerCase().includes(artistFilter.toLowerCase());
        const matchesMedium = !mediumFilter ||
            artwork.medium?.toLowerCase().includes(mediumFilter.toLowerCase());
        const matchesCollection = !collectionFilter ||
            collectionLabel?.toLowerCase().includes(collectionFilter.toLowerCase());
        const matchesSearch = !searchFilter ||
            artwork.title?.toLowerCase().includes(searchFilter.toLowerCase()) ||
            artwork.artist?.toLowerCase().includes(searchFilter.toLowerCase()) ||
            artwork.medium?.toLowerCase().includes(searchFilter.toLowerCase()) ||
            collectionLabel?.toLowerCase().includes(searchFilter.toLowerCase());
        return matchesArtist && matchesMedium && matchesCollection && matchesSearch;
    });
    const uniqueArtists = Array.from(new Set(artworks.map((artwork) => artwork.artist).filter(Boolean))).sort((a, b) => a.localeCompare(b, getLocaleCode()));
    const uniqueMediums = Array.from(new Set(artworks.map((artwork) => artwork.medium).filter(Boolean))).sort((a, b) => getMediumLabel(a, t).localeCompare(getMediumLabel(b, t), getLocaleCode()));
    const uniqueCollections = Array.from(new Set(artworks.map(getArtworkCollectionLabel).filter(Boolean))).sort((a, b) => a.localeCompare(b, getLocaleCode()));
    const resetArtworkFilters = () => {
        setSearchFilter("");
        setArtistFilter("");
        setMediumFilter("");
        setCollectionFilter("");
    };
    const loadArtworks = async () => {
        try {
            const { data, error } = await supabase
                .from("artworks")
                .select("*")
                .eq("client_id", user.id)
                .order("title", { ascending: true });
            if (error)
                throw error;
            setArtworks(data || []);
        }
        catch (error) {
            console.error("Erreur chargement œuvres:", error);
        }
    };
    const handleArtworkSelect = (artwork) => {
        setSelectedArtwork(artwork);
        setCurrentStep(2);
    };
    const getPrimaryImage = (images) => {
        if (!images || images.length === 0)
            return null;
        const parsed = typeof images === "string" ? JSON.parse(images) : images;
        const primary = parsed.find((img) => img.isPrimary);
        const url = primary?.url ||
            primary?.path ||
            parsed[0]?.url ||
            parsed[0]?.path ||
            null;
        return url ? getOptimizedThumbnailUrl(url, 400) : null;
    };
    const getOptimizedThumbnailUrl = (url, width = 400) => {
        if (!url)
            return null;
        // Transform Supabase storage URLs to use the image render endpoint
        const match = url.match(/^(https:\/\/[^/]+\.supabase\.co)\/storage\/v1\/object\/public\/(.+)$/);
        if (match) {
            return `${match[1]}/storage/v1/render/image/public/${match[2]}?width=${width}&height=${width}&resize=cover&quality=75`;
        }
        return url;
    };
    // Méthodes d'estimation (textes via i18n)
    const estimationMethods = [
        {
            id: "points",
            icon: Calculator,
            title: t("estimation.methods.points.title"),
            description: t("estimation.methods.points.description"),
            whenToUse: t("estimation.methods.points.whenToUse"),
            features: [
                t("estimation.methods.points.feature1"),
                t("estimation.methods.points.feature2"),
                t("estimation.methods.points.feature3"),
                t("estimation.methods.points.feature4"),
            ],
            duration: t("estimation.methods.points.duration"),
            cost: t("estimation.methods.points.cost"),
            path: "/estimation-point",
            color: "blue",
            recommended: selectedArtwork?.estimated_value === null ||
                selectedArtwork?.estimated_value === 0,
        },
        {
            id: "comparables",
            icon: TrendingUp,
            title: t("estimation.methods.comparables.title"),
            description: t("estimation.methods.comparables.description"),
            whenToUse: t("estimation.methods.comparables.whenToUse"),
            features: [
                t("estimation.methods.comparables.feature1"),
                t("estimation.methods.comparables.feature2"),
                t("estimation.methods.comparables.feature3"),
                t("estimation.methods.comparables.feature4"),
            ],
            duration: t("estimation.methods.comparables.duration"),
            cost: t("estimation.methods.comparables.cost"),
            path: "/estimation-semblables",
            color: "green",
            recommended: !!(selectedArtwork?.artist && selectedArtwork.artist !== ""),
        },
        {
            id: "assurance",
            icon: Shield,
            title: t("estimation.methods.assurance.title"),
            description: t("estimation.methods.assurance.description"),
            whenToUse: t("estimation.methods.assurance.whenToUse"),
            features: [
                t("estimation.methods.assurance.feature1"),
                t("estimation.methods.assurance.feature2"),
                t("estimation.methods.assurance.feature3"),
                t("estimation.methods.assurance.feature4"),
            ],
            duration: t("estimation.methods.assurance.duration"),
            cost: t("estimation.methods.assurance.cost"),
            path: "/estimation-assurance",
            color: "amber",
            recommended: false,
        },
        {
            id: "succession",
            icon: Scale,
            title: t("estimation.methods.succession.title"),
            description: t("estimation.methods.succession.description"),
            whenToUse: t("estimation.methods.succession.whenToUse"),
            features: [
                t("estimation.methods.succession.feature1"),
                t("estimation.methods.succession.feature2"),
                t("estimation.methods.succession.feature3"),
                t("estimation.methods.succession.feature4"),
            ],
            duration: t("estimation.methods.succession.duration"),
            cost: t("estimation.methods.succession.cost"),
            path: "/estimation-succession",
            color: "purple",
            recommended: false,
        },
    ];
    const steps = [
        { num: 1, label: t("estimation.steps.chooseArtwork") },
        { num: 2, label: t("estimation.steps.chooseMethod") },
    ];
    return (<div className="min-h-screen bg-gray-50">
      {/* Header élégant style Artsy */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <h1 className="text-5xl font-light tracking-tight mb-3">
            {t("estimation.page.title")}
          </h1>
          <p className="text-lg text-gray-600 font-light">
            {t("estimation.page.subtitle")}
          </p>
        </div>
      </header>

      {/* Indicateur d'étapes */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center gap-6 max-w-2xl mx-auto">
          {steps.map((step, idx) => (<React.Fragment key={step.num}>
              <div className="flex items-center gap-3">
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center
                  font-medium transition-all duration-300
                  ${currentStep >= step.num
                ? "bg-black text-white"
                : "bg-gray-200 text-gray-400"}
                `}>
                  {step.num}
                </div>
                <span className={`text-base font-light ${currentStep >= step.num
                ? "text-black"
                : "text-gray-400"}`}>
                  {step.label}
                </span>
              </div>
              {idx < steps.length - 1 && (<div className={`flex-1 h-px ${currentStep > step.num ? "bg-black" : "bg-gray-200"}`}/>)}
            </React.Fragment>))}
        </div>
      </div>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-6 py-8 pb-20">
        {/* ========== ÉTAPE 1 : Sélection de l'œuvre ========== */}
        {currentStep === 1 && (<div className="bg-white rounded-sm border border-gray-200 p-10">
            <h2 className="text-3xl font-light mb-8">
              {t("estimation.step1.title")}
            </h2>

            {artworks.length === 0 ? (<div className="text-center py-16">
                <p className="text-gray-600 mb-6 font-light text-lg">
                  {t("estimation.step1.empty")}
                </p>
                <Link to="/artwork/new" className="inline-block px-8 py-3 bg-black text-white rounded-sm hover:bg-gray-800 transition font-light">
                  {t("estimation.step1.addArtwork")}
                </Link>
              </div>) : (<>
                <div className="mb-8 rounded-sm border border-gray-200 bg-gray-50 p-5">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900"><Trans i18nKey="estimation.ui.filtrerLaSelection"/> </p>
                        <p className="text-xs text-gray-500 font-light"><Trans i18nKey="estimation.ui.retrouvezRapidementUneUvreParTitreArtisteTechniqueOu"/> </p>
                      </div>

                      <button type="button" onClick={resetArtworkFilters} className="text-xs underline text-gray-600 hover:text-black"><Trans i18nKey="estimation.ui.reinitialiser"/> </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <input type="text" placeholder={i18next.t("estimation.ui.rechercherTitreArtiste")} value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} className="w-full border border-gray-300 bg-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black"/>

                      <select value={artistFilter} onChange={(e) => setArtistFilter(e.target.value)} className="w-full border border-gray-300 bg-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black">
                        <option value=""><Trans i18nKey="estimation.ui.tousLesArtistes"/></option>
                        {uniqueArtists.map((artist) => (<option key={artist} value={artist}>
                            {artist}
                          </option>))}
                      </select>

                      <select value={mediumFilter} onChange={(e) => setMediumFilter(e.target.value)} className="w-full border border-gray-300 bg-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black">
                        <option value=""><Trans i18nKey="estimation.ui.toutesLesTechniques"/></option>
                        {uniqueMediums.map((medium) => (<option key={medium} value={medium}>
                            {getMediumLabel(medium, t)}
                          </option>))}
                      </select>

                      <select value={collectionFilter} onChange={(e) => setCollectionFilter(e.target.value)} className="w-full border border-gray-300 bg-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-black">
                        <option value=""><Trans i18nKey="estimation.ui.toutesLesSousCollections"/></option>
                        {uniqueCollections.map((collection) => (<option key={collection} value={collection}>
                            {collection}
                          </option>))}
                      </select>
                    </div>

                    <div className="text-xs text-gray-500">
                      {i18next.t("estimation.ui.filterResults", { shown: filteredArtworks.length, total: artworks.length })}
                    </div>
                  </div>
                </div>

                {filteredArtworks.length === 0 ? (<div className="text-center py-16 border border-dashed border-gray-300 rounded-sm">
                    <p className="text-gray-600 font-light mb-4"><Trans i18nKey="estimation.ui.aucuneUvreNeCorrespondAuxFiltres"/> </p>
                    <button type="button" onClick={resetArtworkFilters} className="px-5 py-2 bg-black text-white rounded-sm text-sm hover:bg-gray-800"><Trans i18nKey="estimation.ui.voirToutesLesUvres"/> </button>
                  </div>) : (<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {filteredArtworks.map((artwork) => (<button key={artwork.id} onClick={() => handleArtworkSelect(artwork)} className="group text-left border border-gray-200 rounded-sm overflow-hidden hover:border-black hover:shadow-lg transition-all duration-300">
                    {/* Image de l'œuvre */}
                    <div className="aspect-square bg-gray-100 overflow-hidden relative">
                      {getPrimaryImage(artwork.images) ? (<img src={getPrimaryImage(artwork.images)} alt={artwork.title} loading="lazy" width={400} height={400} className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>) : (<div className="w-full h-full flex items-center justify-center text-gray-400 text-5xl">
                          🖼️
                        </div>)}
                      {/* Badge si déjà estimée */}
                      {artwork.estimated_value > 0 && (<div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-sm font-medium">
                          {t("estimation.artwork.badge.estimated")}
                        </div>)}
                    </div>

                    {/* Informations */}
                    <div className="p-4">
                      <h3 className="font-medium mb-1 group-hover:underline line-clamp-2">
                        {artwork.title ||
                            t("estimation.artwork.untitled")}
                      </h3>
                      {artwork.artist && (<p className="text-sm text-gray-600 font-light mb-2">
                          {artwork.artist}
                        </p>)}
                      {artwork.estimated_value > 0 ? (<p className="text-sm text-green-700 font-medium">
                          {artwork.estimated_value.toLocaleString(getLocaleCode())}{" "}
                          €
                        </p>) : (<p className="text-xs text-gray-400 italic">
                          {t("estimation.artwork.noEstimate")}
                        </p>)}
                    </div>
                  </button>))}
                  </div>)}
              </>)}
          </div>)}

        {/* ========== ÉTAPE 2 : Toutes les méthodes d'estimation ========== */}
        {currentStep === 2 && selectedArtwork && (<>
            {/* Œuvre sélectionnée - Récapitulatif */}
            <div className="bg-white rounded-sm border border-gray-200 p-8 mb-8">
              <div className="flex items-start justify-between">
                <div className="flex gap-6">
                  {/* Miniature */}
                  <div className="w-32 h-32 bg-gray-100 rounded-sm overflow-hidden flex-shrink-0">
                    {getPrimaryImage(selectedArtwork.images) ? (<img src={getPrimaryImage(selectedArtwork.images)} alt={selectedArtwork.title} className="w-full h-full object-cover"/>) : (<div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl">
                        🖼️
                      </div>)}
                  </div>

                  {/* Informations */}
                  <div>
                    <h2 className="text-2xl font-light mb-2">
                      {selectedArtwork.title ||
                t("estimation.artwork.untitled")}
                    </h2>
                    {selectedArtwork.artist && (<p className="text-gray-600 mb-1">
                        {selectedArtwork.artist}
                      </p>)}
                    {selectedArtwork.medium && (<p className="text-sm text-gray-500 font-light">
                        {getMediumLabel(selectedArtwork.medium, t)}
                      </p>)}
                    {selectedArtwork.dimensions && (<p className="text-sm text-gray-500 font-light">
                        {selectedArtwork.dimensions}
                      </p>)}
                    {selectedArtwork.estimated_value > 0 && (<div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-sm text-sm">
                        <CheckCircle size={16}/>
                        <span>
                          {t("estimation.artwork.lastEstimate", {
                    value: selectedArtwork.estimated_value.toLocaleString(getLocaleCode()),
                })}
                        </span>
                      </div>)}
                  </div>
                </div>

                {/* Bouton retour */}
                <button onClick={() => {
                setCurrentStep(1);
                setSelectedArtwork(null);
            }} className="flex items-center gap-2 text-gray-600 hover:text-black transition font-light">
                  <ChevronLeft size={20}/>
                  {t("estimation.selected.changeArtwork")}
                </button>
              </div>
            </div>

            {/* Titre section méthodes */}
            <div className="mb-8">
              <h2 className="text-3xl font-light mb-3">
                {t("estimation.methods.title")}
              </h2>
              <p className="text-gray-600 font-light text-lg">
                {t("estimation.methods.subtitle")}
              </p>
            </div>

            {/* Grille des méthodes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
              {estimationMethods.map((method) => {
                const Icon = method.icon;
                const colorClasses = {
                    blue: "border-l-blue-500 bg-blue-50",
                    green: "border-l-green-500 bg-green-50",
                    amber: "border-l-amber-500 bg-amber-50",
                    purple: "border-l-purple-500 bg-purple-50",
                };
                const accentColors = {
                    blue: "text-blue-700",
                    green: "text-green-700",
                    amber: "text-amber-700",
                    purple: "text-purple-700",
                };
                return (<div key={method.id} className={`
                      bg-white border-l-4 ${colorClasses[method.color].split(" ")[0]}
                      border border-gray-200 rounded-sm p-8 hover:shadow-xl
                      transition-all duration-300 relative group
                    `}>
                    {/* Badge recommandé */}
                    {method.recommended && (<div className="absolute -top-3 right-6 bg-black text-white text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1">
                        <Zap size={12}/>
                        {t("estimation.methods.recommended.badge")}
                      </div>)}

                    {/* Icône et titre */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`${colorClasses[method.color]} p-3 rounded-lg`}>
                          <Icon size={24} className={accentColors[method.color]} strokeWidth={1.5}/>
                        </div>
                        <h3 className="text-xl font-medium">
                          {method.title}
                        </h3>
                      </div>

                      {/* Durée et coût */}
                      <div className="text-right text-xs text-gray-500">
                        <div className="flex items-center gap-1 justify-end mb-1">
                          <Clock size={12}/>
                          <span>{method.duration}</span>
                        </div>
                        <div className="font-medium text-gray-700">
                          {method.cost}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-700 font-light leading-relaxed mb-4">
                      {method.description}
                    </p>

                    {/* Quand l'utiliser */}
                    <div className="mb-4 p-4 bg-gray-50 rounded-md border border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                        {t("estimation.methods.whenToUseLabel")}
                      </p>
                      <p className="text-sm text-gray-700 font-light leading-relaxed">
                        {method.whenToUse}
                      </p>
                    </div>

                    {/* Points clés */}
                    <ul className="space-y-2 mb-6">
                      {method.features.map((feature, idx) => (<li key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle size={16} className={`mt-0.5 flex-shrink-0 ${accentColors[method.color]}`}/>
                          <span className="text-gray-700 font-light">
                            {feature}
                          </span>
                        </li>))}
                    </ul>

                    {/* Bouton d'action */}
                    {method.isExternal ? (<a href={method.path} className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-black text-white rounded-sm hover:bg-gray-800 transition font-light group-hover:gap-3">
                        {t("estimation.methods.button.quote")}
                        <ArrowRight size={18} className="transition-transform"/>
                      </a>) : (<Link to={`${method.path}/${selectedArtwork.id}`} state={{ artwork: selectedArtwork }} className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-black text-white rounded-sm hover:bg-gray-800 transition font-light group-hover:gap-3">
                        {t("estimation.methods.button.start")}
                        <ArrowRight size={18} className="transition-transform"/>
                      </Link>)}
                  </div>);
            })}
            </div>

            {/* Guide contextuel en bas */}
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-sm p-10">
              <h3 className="text-2xl font-light mb-6">
                {t("estimation.guide.title")}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div className="border-l-2 border-blue-400 pl-4">
                  <p className="font-medium mb-2">
                    {t("estimation.guide.assurance.title")}
                  </p>
                  <p className="text-gray-300 font-light leading-relaxed">
                    {t("estimation.guide.assurance.text")}
                  </p>
                </div>

                <div className="border-l-2 border-green-400 pl-4">
                  <p className="font-medium mb-2">
                    {t("estimation.guide.sale.title")}
                  </p>
                  <p className="text-gray-300 font-light leading-relaxed">
                    {t("estimation.guide.sale.text")}
                  </p>
                </div>

                <div className="border-l-2 border-purple-400 pl-4">
                  <p className="font-medium mb-2">
                    {t("estimation.guide.succession.title")}
                  </p>
                  <p className="text-gray-300 font-light leading-relaxed">
                    {t("estimation.guide.succession.text")}
                  </p>
                </div>

                <div className="border-l-2 border-indigo-400 pl-4">
                  <p className="font-medium mb-2">
                    {t("estimation.guide.highValue.title")}
                  </p>
                  <p className="text-gray-300 font-light leading-relaxed">
                    {t("estimation.guide.highValue.text")}
                  </p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white border-opacity-20">
                <p className="text-sm text-gray-400 font-light">
                  {t("estimation.guide.note")}
                </p>
              </div>
            </div>

            {/* Services professionnels Artkiv */}
            <div className="mt-16 bg-gradient-to-br from-black to-gray-900 text-white rounded-sm p-10">
              <div className="mb-8">
                <h2 className="text-3xl font-light mb-3">
                  {t("estimation.pro.title")}
                </h2>
                <p className="text-gray-400 font-light text-lg">
                  {t("estimation.pro.subtitle")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Service 1 : Validation Artkiv */}
                <div className="bg-white bg-opacity-10 backdrop-blur rounded-sm p-6 border border-white border-opacity-20 hover:bg-opacity-15 transition">
                  <div className="flex items-center gap-2 mb-4">
                    <Users size={24} strokeWidth={1.5}/>
                    <h3 className="text-xl font-light">
                      {t("estimation.pro.validation.title")}
                    </h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 font-light">
                    {t("estimation.pro.validation.desc")}
                  </p>
                  <ul className="space-y-2 text-sm text-gray-300 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0"/>
                      <span className="font-light">
                        {t("estimation.pro.validation.point1")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0"/>
                      <span className="font-light">
                        {t("estimation.pro.validation.point2")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0"/>
                      <span className="font-light">
                        {t("estimation.pro.validation.point3")}
                      </span>
                    </li>
                  </ul>
                  <Link to="/estimation/validation-info" className="block w-full px-6 py-3 bg-white text-black rounded-sm hover:bg-gray-100 transition font-light text-center">
                    {t("estimation.pro.validation.button")}
                  </Link>
                </div>

                {/* Service 2 : Expertise CNES */}
                <div className="bg-white bg-opacity-10 backdrop-blur rounded-sm p-6 border border-white border-opacity-20 hover:bg-opacity-15 transition">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText size={24} strokeWidth={1.5}/>
                    <h3 className="text-xl font-light">
                      {t("estimation.pro.expertise.title")}
                    </h3>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed mb-4 font-light">
                    {t("estimation.pro.expertise.desc")}
                  </p>
                  <ul className="space-y-2 text-sm text-gray-300 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0"/>
                      <span className="font-light">
                        {t("estimation.pro.expertise.point1")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0"/>
                      <span className="font-light">
                        {t("estimation.pro.expertise.point2")}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 flex-shrink-0"/>
                      <span className="font-light">
                        {t("estimation.pro.expertise.point3")}
                      </span>
                    </li>
                  </ul>
                  <a href="mailto:experts@artkiv.com" className="block w-full px-6 py-3 bg-white text-black rounded-sm hover:bg-gray-100 transition font-light text-center">
                    {t("estimation.pro.expertise.button")}
                  </a>
                </div>
              </div>

              <div className="text-center pt-6 border-t border-white border-opacity-20">
                <p className="text-gray-400 text-sm font-light">
                  {t("estimation.pro.footer")}
                </p>
              </div>
            </div>
          </>)}
      </main>
    </div>);
}
